import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Play, Pause, SkipForward, Check, X, ArrowLeft } from "lucide-react";

const FOCUS_MIN = 25;
const BREAK_MIN = 5;

export default function FocusMode() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [mode, setMode] = useState("focus"); // focus | break
  const [remaining, setRemaining] = useState(FOCUS_MIN * 60);
  const [running, setRunning] = useState(false);
  const [completedCycles, setCompletedCycles] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // No single GET — find via week range. Pull from week_start of last 4 weeks.
        const today = new Date();
        const monday = new Date(today);
        monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
        for (let off = 0; off <= 8 && !cancelled; off += 7) {
          const d = new Date(monday);
          d.setDate(d.getDate() - off);
          const ws = d.toISOString().slice(0, 10);
          const r = await api.get(`/weekly-tasks?week_start=${ws}`);
          const found = r.data.find((t) => t.id === taskId);
          if (found) { setTask(found); return; }
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [taskId]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          // cycle complete
          if (mode === "focus") {
            setMode("break");
            setCompletedCycles((c) => c + 1);
            // Log the completed focus session (fire and forget)
            api.post("/focus/session", {
              task_id: task?.id || null,
              task_title: task?.title || "",
              duration_min: FOCUS_MIN,
              completed: true,
            }).catch(() => {});
            return BREAK_MIN * 60;
          } else {
            setMode("focus");
            return FOCUS_MIN * 60;
          }
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [running, mode, task]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const total = mode === "focus" ? FOCUS_MIN * 60 : BREAK_MIN * 60;
  const pct = ((total - remaining) / total) * 100;

  const reset = () => {
    setRunning(false);
    setMode("focus");
    setRemaining(FOCUS_MIN * 60);
  };

  const markDone = async () => {
    if (!task) return;
    // If user manually completes mid-cycle, give them credit for the
    // elapsed time as a partial focus session (still counts toward streak).
    const elapsedMin = Math.max(
      1,
      Math.round((FOCUS_MIN * 60 - remaining) / 60),
    );
    api.post("/focus/session", {
      task_id: task.id,
      task_title: task.title || "",
      duration_min: elapsedMin,
      completed: true,
    }).catch(() => {});
    // Spread the task so optional fields (rationale, auto_rescheduled, etc.)
    // are preserved on update.
    await api.put(`/weekly-tasks/${task.id}`, {
      ...task,
      done: true,
    });
    navigate("/board");
  };

  return (
    <div className="fixed inset-0 z-50 bg-[hsl(var(--background))] flex flex-col" data-testid="focus-mode">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <button
          onClick={() => navigate(-1)}
          className="hud-btn !text-[10px]"
          data-testid="focus-exit"
        >
          <ArrowLeft size={11} /> EXIT
        </button>
        <div className="mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
          Focus mode · {completedCycles} cycle{completedCycles === 1 ? "" : "s"} complete
        </div>
        <div className="w-[80px]" />
      </div>

      {/* Centerpiece */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-xl w-full text-center">
          <div className="kpi-label">
            {mode === "focus" ? "Deep work" : "Recover"}
          </div>
          <h1 className="display text-2xl sm:text-4xl mt-3 font-bold uppercase tracking-tight leading-tight">
            {task?.title || "Loading task…"}
          </h1>
          {task?.rationale && (
            <p className="text-sm text-muted-foreground italic mt-3 max-w-md mx-auto">
              {task.rationale}
            </p>
          )}

          <div className="hairline-oud my-10 max-w-xs mx-auto" />

          <div
            className="hud font-bold text-7xl sm:text-9xl tabular-nums"
            style={{ color: mode === "focus" ? "hsl(var(--oxblood))" : "hsl(var(--oud))" }}
            data-testid="focus-timer"
          >
            {mm}:{ss}
          </div>

          {/* Progress ring */}
          <div className="mt-6 h-1 max-w-md mx-auto bg-border relative overflow-hidden">
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${pct}%`,
                background: mode === "focus" ? "hsl(var(--oxblood))" : "hsl(var(--oud))",
              }}
            />
          </div>

          {/* Controls */}
          <div className="mt-10 flex items-center justify-center gap-3">
            <button
              onClick={() => setRunning((r) => !r)}
              className="hud-btn hud-btn-primary !px-6 !py-4 !text-xs"
              data-testid="focus-toggle"
            >
              {running ? <><Pause size={13} /> PAUSE</> : <><Play size={13} /> {remaining === FOCUS_MIN * 60 ? "START" : "RESUME"}</>}
            </button>
            <button
              onClick={() => {
                setRunning(false);
                if (mode === "focus") { setMode("break"); setRemaining(BREAK_MIN * 60); }
                else { setMode("focus"); setRemaining(FOCUS_MIN * 60); }
              }}
              className="hud-btn !text-xs"
              data-testid="focus-skip"
            >
              <SkipForward size={11} /> SKIP
            </button>
            <button onClick={reset} className="hud-btn !text-xs">
              <X size={11} /> RESET
            </button>
          </div>

          <button
            onClick={markDone}
            className="hud-btn mt-8 !text-[10px] border-oud text-oud hover:bg-[hsl(var(--oud)/0.15)]"
            data-testid="focus-done"
          >
            <Check size={11} /> MARK TASK COMPLETE
          </button>

          {task && !task.scheduled_date && (
            <div className="mt-8">
              <Link to="/board" className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">
                Open week board →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
